import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { generateApiKey, hashApiKey } from "../../../../shared/apiKeys";
import { USER_API_KEY_NAME_PREFIX } from "../../../../shared/apiKeyPrefix";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import type { ShareableScopeKind } from "../../../../shared/rbac/permissions";
import {
  type GrantEntry,
  type RoleOption,
  rolesForScope,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../../../shared/rbac/roles";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import { getWritableScopeIds } from "../../../lib/getWritableScopeIds";
import { computeSpaceMemberSupplierGap } from "../../../lib/spaceMemberSupplierGap";
import { usedSkillsForSpace } from "../../../lib/usedSuppliersForSpace";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../../../hooks/useConnectorDisconnectImpact";
import { useGrantSpaceAccess } from "../../../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useGroupRemovalDisconnectGuard } from "../../../hooks/useGroupRemovalDisconnectGuard";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useCurrentUserId } from "../../../hooks/useCurrentUserId";
import { useQuery } from "../../../hooks/useQuery";
import { useSpaceUsedSuppliers } from "../../../hooks/useSpaceUsedSuppliers";
import { formatTimeUntil } from "../../../lib/formatRelativeTime";
import { formatDateShort } from "../../../lib/formatTimestamp";
import {
  customRolesForScope,
  grantEntriesToRows,
  grantedGroupIds,
} from "../../../lib/grantAssembly";
import { collectRoleChips, type ResolveScopeName } from "../../../lib/roleChip";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { userDisplayName } from "../../../lib/userDisplayName";
import { useOpenResource } from "../lib/useOpenResource";
import { AccessMapPanel } from "../../AccessMapPanel";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { PressButton } from "../../PressButton";
import { Count, DisconnectWarning } from "../../DisconnectWarning";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { ListTable } from "../../ListTable";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { ScopeAccessTable } from "../../ScopeAccessTable";
import { TabbedModal } from "../../TabbedModal";
import { Text } from "../../Text";
import { CreateApiKeyModal } from "../ApiKeysSettings/CreateApiKeyModal";
import { RoleBadgeList } from "../RoleBadgeList";
import { AvatarUploader } from "./AvatarUploader";
import { MemberAccessTable } from "./MemberAccessTable";
import {
  Card,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  GROUP_TOOLTIP_COPY,
  HelpTooltip,
  ListBox,
  NeutralIconBadge,
  SectionHeading,
} from "../shared";
import { DangerSection } from "../DangerSection";

const ViewButton = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border-radius: 8px;
  background: var(--alpha4);
  color: var(--text-secondary);
  &:hover {
    background: var(--alpha8);
    color: var(--text-primary);
  }
`;

const SectionHeadingRow = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SectionHeadingText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NameCell = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const NameCellText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

function keyDisplayName(name: string | null): string {
  return name?.startsWith(USER_API_KEY_NAME_PREFIX)
    ? name.slice(USER_API_KEY_NAME_PREFIX.length)
    : (name ?? "API key");
}

type Tab = "membership" | "access";
const TABS = [
  { key: "membership", label: "Membership", icon: "id-card" },
  { key: "access", label: "Access", icon: "lock" },
] as const;

type SpaceMember = QueryRowType<ReturnType<typeof queries.allUsers>>;
type UserGroup = QueryRowType<ReturnType<typeof queries.allUserGroups>>;
type Connector = QueryRowType<ReturnType<typeof queries.connectedAppsIndex>>;
type Space = QueryRowType<ReturnType<typeof queries.spaces>>;
type Skill = QueryRowType<ReturnType<typeof queries.skillsIndex>>;
type CustomRole = QueryRowType<ReturnType<typeof queries.allRolesLite>>;

export function MemberDetailModal({
  member,
  groups,
  customRoles,
  apps,
  allSpaces,
  allSkills,
  resolveScopeName,
  personalSpaceId,
  canManageUsers,
  idpSyncLocked,
  idpSyncReason,
  groupMembershipLocked,
  groupMembershipReason,
  onClose,
  onDeactivate,
  onReactivate,
}: {
  member: SpaceMember;
  groups: readonly UserGroup[];
  customRoles: readonly CustomRole[];
  apps: readonly Connector[];
  allSpaces: readonly Space[];
  allSkills: readonly Skill[];
  resolveScopeName: ResolveScopeName;
  personalSpaceId: string | undefined;
  canManageUsers: boolean;
  idpSyncLocked: boolean;
  idpSyncReason: string | null;
  groupMembershipLocked: boolean;
  groupMembershipReason: string | null;
  onClose: () => void;
  onDeactivate?: (member: SpaceMember) => void;
  onReactivate?: (member: SpaceMember) => void;
}) {
  const z = useZero();
  const currentUserId = useCurrentUserId();
  const isSelf = member.id === currentUserId;
  const { tab } = useCurrentUrl(routes.modal.member, { id: member.id });
  const activeTab = tab as Tab;
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [confirmRevokeKey, setConfirmRevokeKey] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [memberKeys] = useQuery(queries.userApiKeys({ userId: member.id }));
  const { openResource } = useOpenResource();
  const { guard: guardSpaceAccess, modal: grantAccessModal } =
    useGrantSpaceAccess();
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const {
    stagedGrantsRef: groupStagedRef,
    requestClose: requestCloseGroups,
    closeWarningModal: groupsCloseWarning,
  } = useScopeAccessCloseWarning({ singular: "group", plural: "groups" });
  const {
    stagedGrantsRef: accessStagedRef,
    requestClose: requestCloseAccess,
    closeWarningModal: accessCloseWarning,
  } = useScopeAccessCloseWarning({ singular: "resource", plural: "resources" });
  const [confirmingSelfRemoveGroupId, setConfirmingSelfRemoveGroupId] =
    useState<string | null>(null);
  const [confirmingSelfRemoveAccess, setConfirmingSelfRemoveAccess] = useState<{
    scopeId: string;
    scopeKind: ShareableScopeKind;
    desired: GrantEntry[];
  } | null>(null);
  const [pendingConnectorRemoval, setPendingConnectorRemoval] = useState<{
    appId: string;
    desired: GrantEntry[];
  } | null>(null);
  const [memberGrants] = useQuery(
    queries.roleGrantsByUser({ userId: member.id })
  );
  const customRoleById = useMemo(
    () => new Map(customRoles.map((role) => [role.id, role])),
    [customRoles]
  );
  const groupById = useMemo(
    () => new Map(groups.map((g) => [g.id, g])),
    [groups]
  );
  const groupCustomRoles = useMemo(
    () => customRolesForScope(customRoles, "user_group"),
    [customRoles]
  );
  const roleOptions: RoleOption[] = useMemo(
    () => rolesForScope("user_group", groupCustomRoles),
    [groupCustomRoles]
  );
  const memberGroupGrants: GrantEntry[] = useMemo(
    () =>
      memberGrants.flatMap((grant) =>
        groupById.has(grant.scopeId)
          ? [
              {
                userGroupId: grant.scopeId,
                builtinRoleId: grant.builtinRoleId ?? undefined,
                customRoleId: grant.customRoleId ?? undefined,
              },
            ]
          : []
      ),
    [groupById, memberGrants]
  );
  const assignedGroups = useMemo(() => {
    const groupIds = grantedGroupIds(memberGroupGrants);
    return groups.filter((g) => groupIds.has(g.id));
  }, [groups, memberGroupGrants]);
  const inheritedGrants = useMemo(
    () =>
      assignedGroups.flatMap((group) =>
        (group.roleGrantsAsPrincipal ?? []).map((g) => ({
          scopeId: g.scopeId,
          builtinRoleId: g.builtinRoleId,
          customRoleId: g.customRoleId,
          customRole: g.customRoleId
            ? (customRoleById.get(g.customRoleId) ?? null)
            : null,
          viaGroupId: group.id,
          viaGroupName: group.name,
        }))
      ),
    [assignedGroups, customRoleById]
  );
  const buildGroupDesired = useCallback(
    (group: UserGroup, userId: string, desiredRoles: GrantEntry[]) => [
      ...(group.roleGrantsAsScope ?? [])
        .filter((grant) => grant.userId && grant.userId !== userId)
        .map((grant) => ({
          userId: grant.userId!,
          builtinRoleId: grant.builtinRoleId ?? undefined,
          customRoleId: grant.customRoleId ?? undefined,
        })),
      ...desiredRoles.map((grant) => ({
        userId,
        builtinRoleId: grant.builtinRoleId,
        customRoleId: grant.customRoleId,
      })),
    ],
    []
  );
  const commitGrants = useCallback(
    (next: GrantEntry[]) => {
      const roleKeysForGroup = (grants: GrantEntry[], groupId: string) =>
        grants
          .filter((grant) => grant.userGroupId === groupId)
          .map((grant) => grant.builtinRoleId ?? grant.customRoleId ?? "")
          .sort()
          .join("|");
      const groupIds = new Set<string>();
      for (const grant of memberGroupGrants) {
        if (grant.userGroupId) groupIds.add(grant.userGroupId);
      }
      for (const grant of next) {
        if (grant.userGroupId) groupIds.add(grant.userGroupId);
      }
      for (const groupId of groupIds) {
        if (
          roleKeysForGroup(memberGroupGrants, groupId) ===
          roleKeysForGroup(next, groupId)
        )
          continue;
        const group = groupById.get(groupId);
        if (!group) continue;
        z.mutate(
          sharedMutators.setGroupGrants({
            userGroupId: groupId,
            grants: buildGroupDesired(
              group,
              member.id,
              next.filter((grant) => grant.userGroupId === groupId)
            ),
          })
        );
      }
    },
    [buildGroupDesired, groupById, member.id, memberGroupGrants, z]
  );
  const { guard: guardGroupRemoval, modal: disconnectModal } =
    useGroupRemovalDisconnectGuard({
      userGroupIds: useMemo(
        () => assignedGroups.map((g) => g.id),
        [assignedGroups]
      ),
    });
  const removeGroup = useCallback(
    (groupId: string) => {
      if (member.id === currentUserId) {
        setConfirmingSelfRemoveGroupId(groupId);
        return;
      }
      const group = groupById.get(groupId);
      if (!group) return;
      guardGroupRemoval(groupId, [member.id], () => {
        z.mutate(
          sharedMutators.setGroupGrants({
            userGroupId: groupId,
            grants: buildGroupDesired(group, member.id, []),
          })
        );
      });
    },
    [
      buildGroupDesired,
      currentUserId,
      groupById,
      guardGroupRemoval,
      member.id,
      z,
    ]
  );
  const confirmRemoveSelf = useCallback(() => {
    if (!confirmingSelfRemoveGroupId) return;
    const groupId = confirmingSelfRemoveGroupId;
    const group = groupById.get(groupId);
    if (group) {
      guardGroupRemoval(groupId, [member.id], () => {
        z.mutate(
          sharedMutators.setGroupGrants({
            userGroupId: groupId,
            grants: buildGroupDesired(group, member.id, []),
          })
        );
      });
    }
    setConfirmingSelfRemoveGroupId(null);
  }, [
    buildGroupDesired,
    confirmingSelfRemoveGroupId,
    groupById,
    guardGroupRemoval,
    member.id,
    z,
  ]);
  const roleChips = useMemo(
    () =>
      collectRoleChips(
        memberGrants
          .filter((grant) => grant.scopeId !== personalSpaceId)
          .map((grant) => ({
            scopeId: grant.scopeId,
            builtinRoleId: grant.builtinRoleId,
            customRole: grant.customRoleId
              ? customRoleById.get(grant.customRoleId)
              : null,
          })),
        resolveScopeName
      ),
    [customRoleById, memberGrants, personalSpaceId, resolveScopeName]
  );
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadPersonalChats = useHasAccess("tenant:personal_chats_read");
  const canReadApps = useHasAccess("tenant:connected_apps_read");
  const canReadSkills = useHasAccess("tenant:skills_read");
  const [myGrants] = useQuery(queries.currentUserGrants());
  const manageableSpaceIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allSpaces.map((s) => s.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.space
      ),
    [myGrants, allSpaces]
  );
  const manageableAppIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        apps.map((a) => a.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
      ),
    [myGrants, apps]
  );
  const manageableSkillIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allSkills.map((s) => s.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.skill
      ),
    [myGrants, allSkills]
  );
  const { usedConnectorsForSpace } = useSpaceUsedSuppliers(apps);
  const commitScope = useCallback(
    (
      scopeId: string,
      scopeKind: ShareableScopeKind,
      desired: GrantEntry[],
      onCommitted?: () => void
    ) => {
      const doMutate = () => {
        z.mutate(
          sharedMutators.setScopeRoleGrants({
            scopeId,
            scopeKind,
            grants: desired,
          })
        );
      };
      if (scopeKind !== "space") {
        doMutate();
        return;
      }
      const space = allSpaces.find((s) => s.id === scopeId);
      if (!space) {
        doMutate();
        return;
      }
      const spaceGrants = space.roleGrantsAsScope;
      const gaps = computeSpaceMemberSupplierGap({
        currentSpaceGrants: spaceGrants,
        desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
        connectors: usedConnectorsForSpace(scopeId),
        skills: usedSkillsForSpace(spaceGrants, allSkills),
        memberships: userGroupMemberships,
        manageableConnectorIds: manageableAppIds,
        manageableSkillIds,
      });
      if (gaps.length === 0) {
        doMutate();
        return;
      }
      // Deferred: keep the scope staged behind the grant modal and add the
      // member (then run the caller's continuation) only once it confirms.
      guardSpaceAccess({
        spaceName: space.name,
        gaps,
        proceed: () => {
          doMutate();
          onCommitted?.();
        },
      });
      return false;
    },
    [
      z,
      allSpaces,
      allSkills,
      customRoles,
      userGroupMemberships,
      usedConnectorsForSpace,
      manageableAppIds,
      manageableSkillIds,
      guardSpaceAccess,
    ]
  );
  const requestSelfRemoveAccess = useCallback(
    (scopeId: string, scopeKind: ShareableScopeKind, desired: GrantEntry[]) => {
      setConfirmingSelfRemoveAccess({ scopeId, scopeKind, desired });
    },
    []
  );
  const removeConnectorAccess = useCallback(
    (appId: string, desired: GrantEntry[]) => {
      setPendingConnectorRemoval({ appId, desired });
    },
    []
  );
  const computeConnectorImpact = useConnectorDisconnectImpact(
    pendingConnectorRemoval?.appId ?? null
  );
  const pendingConnectorApp = pendingConnectorRemoval
    ? apps.find((a) => a.id === pendingConnectorRemoval.appId)
    : null;
  const connectorRemovalImpact =
    pendingConnectorRemoval && pendingConnectorApp
      ? computeConnectorImpact(
          pendingConnectorApp.roleGrantsAsScope,
          pendingConnectorRemoval.desired
        )
      : null;
  const connectorChatCount = useAffectedChatCount(
    pendingConnectorRemoval?.appId ?? null,
    connectorRemovalImpact?.affectedUserIds ?? []
  );
  const canViewAsAdmin =
    (personalSpaceId && canReadSpaces) || canReadPersonalChats;
  const adminViewLabel =
    personalSpaceId && canReadSpaces && canReadPersonalChats
      ? "Personal and chats"
      : personalSpaceId && canReadSpaces
        ? "Open personal workflows"
        : "Open personal chats";

  const close = () => requestCloseGroups(() => requestCloseAccess(onClose));

  const addKey = async (name: string) => {
    const rawKey = generateApiKey();
    const hashedKey = await hashApiKey(rawKey);
    await z.mutate(
      sharedMutators.createUserApiKey({
        id: nanoid(12),
        name,
        start: rawKey.slice(0, 12),
        key: hashedKey,
      })
    );
    return rawKey;
  };

  const keyItems = memberKeys.map((k) => ({
    key: k.id,
    name: (
      <NameCell>
        <NeutralIconBadge iconName="key" />
        <NameCellText>
          <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
            {keyDisplayName(k.name)}
          </Text>
          <Text ellipsis variant="text-small" color="var(--text-secondary)">
            {k.start ? `${k.start}…` : ""}
          </Text>
        </NameCellText>
      </NameCell>
    ),
    created: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.createdAt ? formatDateShort(k.createdAt) : "—"}
      </Text>
    ),
    lastUsed: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.lastRequest ? formatDateShort(k.lastRequest) : "never"}
      </Text>
    ),
    expires: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.expiresAt ? formatTimeUntil(k.expiresAt) : "never"}
      </Text>
    ),
    actions: (hasMouse: boolean) => (
      <div
        style={{ opacity: hasMouse ? 1 : 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="secondary"
          icon="x"
          onClick={() =>
            setConfirmRevokeKey({ id: k.id, name: keyDisplayName(k.name) })
          }
        />
      </div>
    ),
  }));

  return (
    <>
      <TabbedModal
        icon={<Avatar user={member} size={32} />}
        title={userDisplayName(member)}
        headerRight={
          !isSelf && canViewAsAdmin ? (
            <ViewButton
              onPress={() =>
                requestCloseGroups(() =>
                  requestCloseAccess(() => {
                    onClose();
                    if (personalSpaceId && canReadSpaces) {
                      navigate(routes.space, {
                        spaceId: personalSpaceId,
                      });
                    } else {
                      navigate(routes.chats, {
                        userId: member.id,
                      });
                    }
                  })
                )
              }
            >
              <Text variant="text-small">{adminViewLabel}</Text>
              <Icon name="arrow-up-right" size={12} />
            </ViewButton>
          ) : null
        }
        tabs={TABS.map((t) => ({
          ...t,
          to: makeUrl(routes.modal.member, { id: member.id, tab: t.key }),
        }))}
        activeTab={activeTab}
        onTabChange={(next) =>
          navigate(routes.modal.member, { id: member.id, tab: next })
        }
        onClose={close}
        footerRight={
          <Button size="l" onClick={close}>
            Done
          </Button>
        }
      >
        <TabPanel
          style={{ display: activeTab === "membership" ? "flex" : "none" }}
        >
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Member</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Profile and account details.
              </Text>
            </SectionHeading>
            <DetailList>
              {isSelf && (
                <DetailRow>
                  <DetailLabel>
                    <Text variant="text-regular-md" color="var(--text-primary)">
                      Profile picture
                    </Text>
                  </DetailLabel>
                  <DetailRowRight>
                    <AvatarUploader user={member} size={32} />
                  </DetailRowRight>
                </DetailRow>
              )}
              <DetailRow>
                <DetailLabel>
                  <Text variant="text-regular-md" color="var(--text-primary)">
                    Name
                  </Text>
                </DetailLabel>
                <DetailRowRight>
                  <Input
                    value={member.name ?? ""}
                    disabled
                    placeholder="Full name"
                    style={{ width: 220, minWidth: 220 }}
                  />
                </DetailRowRight>
              </DetailRow>
              <DetailRow>
                <DetailLabel>
                  <Text variant="text-regular-md" color="var(--text-primary)">
                    Email
                  </Text>
                </DetailLabel>
                <DetailRowRight>
                  <Input
                    value={member.email}
                    disabled
                    style={{ width: 220, minWidth: 220 }}
                  />
                </DetailRowRight>
              </DetailRow>
            </DetailList>
          </Card>

          <Card>
            <SectionHeading>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Text variant="text-regular-md">Groups</Text>
                <HelpTooltip content={GROUP_TOOLTIP_COPY} />
              </div>
              <Text variant="text-small" color="var(--text-secondary)">
                Groups this member belongs to.
              </Text>
            </SectionHeading>
            <ScopeAccessTable
              allGrants={memberGroupGrants}
              assignedGroups={assignedGroups}
              assignedUsers={[]}
              roleOptions={roleOptions}
              allGroups={groups}
              allUsers={[]}
              customRoles={groupCustomRoles}
              canManage={canManageUsers}
              compact
              commitGrants={commitGrants}
              removeGroup={removeGroup}
              removeUser={() => {}}
              onOpenGroup={(groupId) =>
                navigate(routes.modal.group, { id: groupId })
              }
              idpSyncLocked={groupMembershipLocked}
              idpSyncReason={groupMembershipReason}
              searchPlaceholder="Add to a group…"
              stagedGrantsRef={groupStagedRef}
              defaultRole={defaultRoleForScope("user_group", roleOptions)}
            />
          </Card>

          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Roles</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Roles assigned directly to this member.
              </Text>
            </SectionHeading>
            <DetailList>
              {roleChips.length === 0 ? (
                <DetailRow>
                  <NeutralIconBadge iconName="circle-slash" />
                  <DetailLabel>
                    <Text variant="text-regular-md">No direct roles</Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Roles show up when assigned directly to this member,
                      rather than through a group.
                    </Text>
                  </DetailLabel>
                </DetailRow>
              ) : (
                <DetailRow>
                  <RoleBadgeList chips={roleChips} />
                </DetailRow>
              )}
            </DetailList>
          </Card>

          {canManageUsers && !member.deactivated && onDeactivate && (
            <DangerSection
              title="Deactivate member"
              description="Deactivates this member and signs them out immediately. You can reactivate them later."
              actionLabel="Deactivate member"
              disabled={idpSyncLocked}
              disabledReason={
                idpSyncLocked ? (idpSyncReason ?? undefined) : undefined
              }
              confirm={{
                confirmLabel: "Deactivate",
                description: `${userDisplayName(member)} will be deactivated and signed out immediately.`,
                onConfirm: () => onDeactivate(member),
              }}
            />
          )}

          {canManageUsers && member.deactivated && onReactivate && (
            <DangerSection
              tone="neutral"
              title="Reactivate member"
              description="Restores this member’s access and lets them sign in again."
              actionLabel="Reactivate member"
              disabled={idpSyncLocked}
              disabledReason={
                idpSyncLocked ? (idpSyncReason ?? undefined) : undefined
              }
              onAction={() => onReactivate(member)}
            />
          )}
        </TabPanel>

        <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
          {!member.deactivated && (
            <AccessMapPanel
              nodeId={member.id}
              noun="member"
              guard={(go) => requestCloseGroups(() => requestCloseAccess(go))}
            />
          )}
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Access</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Spaces, connectors, and skills this member can access, directly
                or through a group.
              </Text>
            </SectionHeading>
            <MemberAccessTable
              memberId={member.id}
              isSelf={isSelf}
              memberDirectGrants={memberGrants}
              inheritedGrants={inheritedGrants}
              spaces={allSpaces}
              apps={apps}
              skills={allSkills}
              customRoles={customRoles}
              resolveScopeName={resolveScopeName}
              personalSpaceId={personalSpaceId}
              manageableSpaceIds={manageableSpaceIds}
              manageableAppIds={manageableAppIds}
              manageableSkillIds={manageableSkillIds}
              canManage={canManageUsers}
              stagedGrantsRef={accessStagedRef}
              hasTenantRead={{
                space: canReadSpaces,
                connected_app: canReadApps,
                skill: canReadSkills,
              }}
              onOpenResource={openResource}
              commitScope={commitScope}
              onRequestSelfRemove={requestSelfRemoveAccess}
              onRemoveConnectorAccess={removeConnectorAccess}
            />
          </Card>

          {(isSelf || (canManageUsers && memberKeys.length > 0)) && (
            <Card>
              <SectionHeading>
                <SectionHeadingRow>
                  <SectionHeadingText>
                    <Text variant="text-regular-md">API keys</Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {isSelf
                        ? "Secret keys that act as you, with your own access. They expire after 24 hours — treat them like passwords."
                        : "Secret keys that act as this member, with their access. They expire after 24 hours."}
                    </Text>
                  </SectionHeadingText>
                  {isSelf && (
                    <Button
                      size="l"
                      variant="secondary"
                      icon="plus"
                      onClick={() => setShowCreateKey(true)}
                    />
                  )}
                </SectionHeadingRow>
              </SectionHeading>
              {memberKeys.length > 0 && (
                <ListBox data-compact="true" style={{ flex: "none" }}>
                  <ListTable
                    headers={{
                      name: "Name",
                      created: "Created",
                      lastUsed: "Last used",
                      expires: "Expires",
                      actions: "",
                    }}
                    columns={{
                      created: "content",
                      lastUsed: "content",
                      expires: "content",
                    }}
                    items={keyItems}
                  />
                </ListBox>
              )}
            </Card>
          )}
        </TabPanel>
      </TabbedModal>

      {groupsCloseWarning}
      {accessCloseWarning}
      {grantAccessModal}
      {disconnectModal}

      <ConfirmModal
        open={confirmingSelfRemoveGroupId !== null}
        onClose={() => setConfirmingSelfRemoveGroupId(null)}
        onConfirm={confirmRemoveSelf}
        title="Leave this group?"
        description="You’ll lose any access this group gave you."
        confirmLabel="Remove"
        destructive
      />

      <ConfirmModal
        open={confirmingSelfRemoveAccess !== null}
        onClose={() => setConfirmingSelfRemoveAccess(null)}
        onConfirm={() => {
          if (confirmingSelfRemoveAccess) {
            commitScope(
              confirmingSelfRemoveAccess.scopeId,
              confirmingSelfRemoveAccess.scopeKind,
              confirmingSelfRemoveAccess.desired
            );
          }
          setConfirmingSelfRemoveAccess(null);
        }}
        title="Remove your own access?"
        description={
          confirmingSelfRemoveAccess
            ? `You’ll lose your direct access to ${
                resolveScopeName(
                  confirmingSelfRemoveAccess.scopeId,
                  confirmingSelfRemoveAccess.scopeKind
                ) ?? "this resource"
              }.`
            : ""
        }
        confirmLabel="Remove"
        destructive
      />

      <ConfirmModal
        open={pendingConnectorRemoval !== null}
        onClose={() => setPendingConnectorRemoval(null)}
        onConfirm={() => {
          if (pendingConnectorRemoval)
            commitScope(
              pendingConnectorRemoval.appId,
              "connected_app",
              pendingConnectorRemoval.desired
            );
          setPendingConnectorRemoval(null);
        }}
        title={isSelf ? "Remove your own access?" : "Remove access?"}
        description={
          connectorRemovalImpact &&
          connectorRemovalImpact.workflows.length > 0 ? (
            <>
              Removing this access means{" "}
              <Count
                n={connectorRemovalImpact.workflows.length}
                noun="workflow"
              />{" "}
              will need a connector replaced.
            </>
          ) : isSelf ? (
            `You’ll lose your direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          ) : (
            `Removes direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          )
        }
        confirmLabel="Remove"
        destructive
      >
        {connectorRemovalImpact && (
          <DisconnectWarning
            {...connectorRemovalImpact}
            chatCount={connectorChatCount}
          />
        )}
      </ConfirmModal>

      {isSelf && showCreateKey && (
        <CreateApiKeyModal
          alert
          showAccess={false}
          onClose={() => setShowCreateKey(false)}
          onCreateKey={addKey}
        />
      )}

      <ConfirmModal
        open={confirmRevokeKey !== null}
        onClose={() => setConfirmRevokeKey(null)}
        onConfirm={() => {
          if (confirmRevokeKey)
            z.mutate(sharedMutators.deleteApiKey({ id: confirmRevokeKey.id }));
          setConfirmRevokeKey(null);
        }}
        title="Revoke API key"
        description={
          confirmRevokeKey
            ? `Anything using ${confirmRevokeKey.name} will stop working immediately.`
            : ""
        }
        confirmLabel="Revoke"
        destructive
      />
    </>
  );
}
